import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import {
    getCurrentAnnualTournament,
    getAnnualTournamentById,
    getAnnualTournamentByYear,
    getAnnualTournamentStats,
    getAnnualTournamentLeaderboard,
    getAnnualWinners,
    registerForAnnualTournament,
    unregisterFromAnnualTournament,
    getRegistrationStatus
} from '../api/annualTournaments';
import { getTournamentBracket } from '../api/tournaments';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbLoader from '../components/controls/HrnbLoader';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import TournamentBracket from '../components/duels/TournamentBracket';
import HrnbModal from '../components/controls/HrnbModal';
import AnnualWinnersList from '../components/tournaments/AnnualWinnersList';
import '../css/pages/AnnualTournamentDetailPage.css';
import {BackIcon, ForwardIcon, RegisterIcon, RejectIcon, TrophyIcon} from "../icons";

/**
 * Страница деталей ежегодного турнира
 */
function AnnualTournamentDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    const [tournament, setTournament] = useState(null);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [leaderboard, setLeaderboard] = useState(null);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [winners, setWinners] = useState([]);
    const [bracket, setBracket] = useState(null);
    const [bracketLoading, setBracketLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showUnregisterModal, setShowUnregisterModal] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [isRegistered, setIsRegistered] = useState(false);
    const [leaderboardPage, setLeaderboardPage] = useState(0);

    // Загрузка данных турнира
    useEffect(() => {
        loadTournamentData();
    }, [id]);

    const loadTournamentData = async () => {
        setLoading(true);
        setError(null);

        try {
            let data;

            // Загружаем турнир по ID или как текущий
            if (id === 'current') {
                data = await getCurrentAnnualTournament();
                if (!data) {
                    throw new Error('Нет активного турнира');
                }
            } else if (id && !isNaN(id)) {
                // ID это год (число)
                data = await getAnnualTournamentByYear(parseInt(id));
            } else if (id) {
                // ID это UUID
                data = await getAnnualTournamentById(id);
            } else {
                data = await getCurrentAnnualTournament();
            }
            
            setTournament(data);
            
            // Загрузка статистики
            if (data.year) {
                setStatsLoading(true);
                getAnnualTournamentStats(data.year)
                    .then(setStats)
                    .catch((err) => {
                        console.warn('Не удалось загрузить статистику:', err);
                    })
                    .finally(() => setStatsLoading(false));

                // Загрузка лидерборда
                setLeaderboardLoading(true);
                getAnnualTournamentLeaderboard(data.year, { size: 20 })
                    .then(setLeaderboard)
                    .catch((err) => {
                        console.warn('Не удалось загрузить лидерборд:', err);
                    })
                    .finally(() => setLeaderboardLoading(false));
            }

            // Загрузка победителей (для вкладки)
            try {
                const winnersList = await getAnnualWinners();
                setWinners(winnersList);
            } catch (err) {
                console.warn('Не удалось загрузить победителей:', err);
            }

            // Проверка регистрации
            if (currentUser && data.year) {
                try {
                    const status = await getRegistrationStatus(data.year);
                    setIsRegistered(!!status.isRegistered);
                } catch (err) {
                    console.warn('Не удалось проверить статус регистрации');
                }
            }
        } catch (err) {
            console.error('Ошибка при загрузке турнира:', err);
            if (err.response?.status === 404) {
                setError('Ежегодный турнир не найден');
            } else {
                setError(err.message || 'Не удалось загрузить турнир. Попробуйте позже.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Загрузка сетки
    useEffect(() => {
        if (id && activeTab === 'bracket') {
            loadBracket();
        }
    }, [id, activeTab]);

    const loadBracket = async () => {
        setBracketLoading(true);
        try {
            const data = await getTournamentBracket(id);
            setBracket(data);
        } catch (err) {
            console.error('Ошибка при загрузке сетки:', err);
        } finally {
            setBracketLoading(false);
        }
    };

    // Загрузка лидерборда при изменении страницы
    useEffect(() => {
        if (tournament?.year && activeTab === 'leaderboard') {
            setLeaderboardLoading(true);
            getAnnualTournamentLeaderboard(tournament.year, { page: leaderboardPage, size: 20 })
                .then(setLeaderboard)
                .catch((err) => {
                    console.warn('Не удалось загрузить лидерборд:', err);
                })
                .finally(() => setLeaderboardLoading(false));
        }
    }, [tournament?.year, activeTab, leaderboardPage]);

    // Регистрация
    const handleRegister = async () => {
        if (!tournament?.year) return;

        setActionLoading(true);
        try {
            await registerForAnnualTournament(tournament.year, {});
            setShowRegisterModal(false);
            setIsRegistered(true);
            loadTournamentData();
        } catch (err) {
            console.error('Ошибка регистрации:', err);
            setError(err.response?.data?.message || 'Не удалось зарегистрироваться');
        } finally {
            setActionLoading(false);
        }
    };

    // Отмена регистрации
    const handleUnregister = async () => {
        if (!tournament?.year) return;

        setActionLoading(true);
        try {
            await unregisterFromAnnualTournament(tournament.year);
            setShowUnregisterModal(false);
            setIsRegistered(false);
            loadTournamentData();
        } catch (err) {
            console.error('Ошибка отмены регистрации:', err);
            setError(err.response?.data?.message || 'Не удалось отменить регистрацию');
        } finally {
            setActionLoading(false);
        }
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Статусы
    const statusLabels = {
        REGISTRATION: 'Регистрация',
        UPCOMING: 'Скоро начнётся',
        ACTIVE: 'Активен',
        COMPLETED: 'Завершён',
        CANCELLED: 'Отменён'
    };

    const statusColors = {
        REGISTRATION: '#22c55e',
        UPCOMING: '#3b82f6',
        ACTIVE: '#8b5cf6',
        COMPLETED: '#6b7280',
        CANCELLED: '#ef4444'
    };

    // Стадии
    const stageLabels = {
        QUALIFICATION: 'Квалификация',
        ROUND_OF_64: '1/64 финала',
        ROUND_OF_32: '1/32 финала',
        ROUND_OF_16: '1/16 финала',
        QUARTER_FINALS: 'Четвертьфиналы',
        SEMI_FINALS: 'Полуфиналы',
        FINALS: 'Финал'
    };

    if (loading) {
        return <HrnbLoader />;
    }

    if (error && !tournament) {
        return (
            <div className="annual-tournament-detail">
                <HrnbAlert variant="danger">{error}</HrnbAlert>
                <Link to="/tournaments/annual">
                    <HrnbButton variant="secondary" icon={<BackIcon />}>Вернуться к списку</HrnbButton>
                </Link>
            </div>
        );
    }

    return (
        <div className="annual-tournament-detail">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbTabs value={activeTab} onChange={setActiveTab}>
                <HrnbTab label="Информация" value="info" />
                <HrnbTab label="Статистика" value="stats" />
                <HrnbTab label="Лидерборд" value="leaderboard" />
                <HrnbTab label="Сетка" value="bracket" />
                <HrnbTab label="Победители" value="winners" />
            </HrnbTabs>

            <div className="annual-tournament-detail__content">
                {activeTab === 'info' && (
                    <>
                        <div className="annual-tournament-detail__header">
                            <div className="annual-tournament-detail__badges">
                                <span 
                                    className="annual-tournament-detail__status"
                                    style={{ backgroundColor: statusColors[tournament.status] }}
                                >
                                    {statusLabels[tournament.status] || tournament.status}
                                </span>
                                {tournament.currentStage && (
                                    <span className="annual-tournament-detail__stage">
                                        {stageLabels[tournament.currentStage] || tournament.currentStage}
                                    </span>
                                )}
                            </div>

                            <h1 className="annual-tournament-detail__title">
                                {tournament.title}
                            </h1>
                            
                            <p className="annual-tournament-detail__year">
                                {tournament.year} год
                            </p>
                        </div>

                        {tournament.description && (
                            <p className="annual-tournament-detail__description">
                                {tournament.description}
                            </p>
                        )}

                        <div className="annual-tournament-detail__meta">
                            <div className="annual-tournament-detail__meta-item">
                                <span className="annual-tournament-detail__meta-label">
                                    Призовой фонд:
                                </span>
                                <span className="annual-tournament-detail__meta-value annual-tournament-detail__meta-value--prize">
                                    {tournament.prizePoolAmount 
                                        ? `${tournament.prizePoolAmount.toLocaleString('ru-RU')} ${tournament.currency || 'RUB'}`
                                        : '—'
                                    }
                                </span>
                            </div>

                            <div className="annual-tournament-detail__meta-item">
                                <span className="annual-tournament-detail__meta-label">
                                    Участников:
                                </span>
                                <span className="annual-tournament-detail__meta-value">
                                    {tournament.participantCount} / {tournament.maxParticipants}
                                </span>
                            </div>

                            <div className="annual-tournament-detail__meta-item">
                                <span className="annual-tournament-detail__meta-label">
                                    Регистрация:
                                </span>
                                <span className="annual-tournament-detail__meta-value">
                                    {formatDate(tournament.registrationStart)} — {formatDate(tournament.registrationEnd)}
                                </span>
                            </div>

                            <div className="annual-tournament-detail__meta-item">
                                <span className="annual-tournament-detail__meta-label">
                                    Начало:
                                </span>
                                <span className="annual-tournament-detail__meta-value">
                                    {formatDate(tournament.startDate)}
                                </span>
                            </div>

                            {tournament.endDate && (
                                <div className="annual-tournament-detail__meta-item">
                                    <span className="annual-tournament-detail__meta-label">
                                        Окончание:
                                    </span>
                                    <span className="annual-tournament-detail__meta-value">
                                        {formatDate(tournament.endDate)}
                                    </span>
                                </div>
                            )}

                            {tournament.winnerName && (
                                <div className="annual-tournament-detail__meta-item">
                                    <span className="annual-tournament-detail__meta-label">
                                        Победитель:
                                    </span>
                                    <span className="annual-tournament-detail__meta-value annual-tournament-detail__meta-value--winner">
                                        🏆 {tournament.winnerName}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Прогресс участников */}
                        <div className="annual-tournament-detail__progress-section">
                            <div className="annual-tournament-detail__progress-label">
                                Заполненность: {tournament.participantCount} из {tournament.maxParticipants}
                            </div>
                            <div className="annual-tournament-detail__progress-bar">
                                <div 
                                    className="annual-tournament-detail__progress-fill"
                                    style={{ width: `${(tournament.participantCount / tournament.maxParticipants) * 100}%` }}
                                />
                            </div>
                        </div>

                        {/* Действия */}
                        <div className="annual-tournament-detail__actions">
                            {tournament.status === 'REGISTRATION' && !isRegistered && (
                                <HrnbButton
                                    variant="success"
                                    onClick={() => setShowRegisterModal(true)}
                                    disabled={actionLoading}
                                    icon={<RegisterIcon />}
                                >
                                    {actionLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                                </HrnbButton>
                            )}

                            {isRegistered && tournament.status === 'REGISTRATION' && (
                                <HrnbButton
                                    variant="danger"
                                    onClick={() => setShowUnregisterModal(true)}
                                    disabled={actionLoading}
                                    icon={<RejectIcon />}
                                >
                                    Отменить регистрацию
                                </HrnbButton>
                            )}

                            {tournament.status === 'ACTIVE' && (
                                <Link to={`/tournaments/annual/${id}/bracket`}>
                                    <HrnbButton variant="primary" icon={<TrophyIcon />}>
                                        Сетка турнира
                                    </HrnbButton>
                                </Link>
                            )}

                            <Link to="/tournaments/annual">
                                <HrnbButton variant="secondary" icon={<BackIcon />}>
                                    Назад к списку
                                </HrnbButton>
                            </Link>
                        </div>
                    </>
                )}

                {activeTab === 'stats' && (
                    <div className="annual-tournament-detail__stats">
                        <h2 className="annual-tournament-detail__section-title">
                            Статистика турнира {tournament?.year}
                        </h2>

                        {statsLoading ? (
                            <HrnbLoader />
                        ) : !stats ? (
                            <div className="annual-tournament-detail__stats-empty">
                                <p>Статистика будет доступна после начала турнира</p>
                            </div>
                        ) : (
                            <>
                                <div className="annual-tournament-detail__stats-grid">
                                    <div className="annual-tournament-detail__stat-card">
                                        <div className="annual-tournament-detail__stat-value">
                                            {stats.totalParticipants ?? tournament.participantCount ?? 0}
                                        </div>
                                        <div className="annual-tournament-detail__stat-label">
                                            Участников
                                        </div>
                                    </div>

                                    <div className="annual-tournament-detail__stat-card">
                                        <div className="annual-tournament-detail__stat-value">
                                            {stats.totalMatches ?? 0}
                                        </div>
                                        <div className="annual-tournament-detail__stat-label">
                                            Матчей
                                        </div>
                                    </div>

                                    <div className="annual-tournament-detail__stat-card">
                                        <div className="annual-tournament-detail__stat-value">
                                            {stats.totalRounds ?? 0}
                                        </div>
                                        <div className="annual-tournament-detail__stat-label">
                                            Раундов
                                        </div>
                                    </div>

                                    <div className="annual-tournament-detail__stat-card">
                                        <div className="annual-tournament-detail__stat-value">
                                            {stats.totalVotes ?? 0}
                                        </div>
                                        <div className="annual-tournament-detail__stat-label">
                                            Голосов
                                        </div>
                                    </div>

                                    {stats.totalTracks != null && (
                                        <div className="annual-tournament-detail__stat-card">
                                            <div className="annual-tournament-detail__stat-value">
                                                {stats.totalTracks}
                                            </div>
                                            <div className="annual-tournament-detail__stat-label">
                                                Треков
                                            </div>
                                        </div>
                                    )}

                                    {stats.averageVotesPerMatch != null && (
                                        <div className="annual-tournament-detail__stat-card">
                                            <div className="annual-tournament-detail__stat-value">
                                                {stats.averageVotesPerMatch?.toFixed(1)}
                                            </div>
                                            <div className="annual-tournament-detail__stat-label">
                                                Среднее голосов / матч
                                            </div>
                                        </div>
                                    )}

                                    {stats.duration?.days != null && stats.duration.days > 0 && (
                                        <div className="annual-tournament-detail__stat-card">
                                            <div className="annual-tournament-detail__stat-value">
                                                {stats.duration.days}
                                            </div>
                                            <div className="annual-tournament-detail__stat-label">
                                                Дней турнира
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Лучшие участники */}
                                {stats.topParticipants && stats.topParticipants.length > 0 && (
                                    <div className="annual-tournament-detail__top-participants">
                                        <h3 className="annual-tournament-detail__subsection-title">
                                            Лучшие участники
                                        </h3>
                                        <div className="annual-tournament-detail__top-list">
                                            {stats.topParticipants.map((participant, index) => (
                                                <div key={participant.profileId || index} className="annual-tournament-detail__top-item">
                                                    <span className="annual-tournament-detail__top-rank">
                                                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                                    </span>
                                                    <span className="annual-tournament-detail__top-name">
                                                        {participant.displayName}
                                                    </span>
                                                    <span className="annual-tournament-detail__top-wins">
                                                        {participant.wins} побед / {participant.matches} матчей
                                                    </span>
                                                    <span className="annual-tournament-detail__top-votes">
                                                        ⭐ {participant.averageVotes?.toFixed(1)} ср.
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Самый обсуждаемый матч */}
                                {stats.mostVotedMatch && (
                                    <div className="annual-tournament-detail__most-voted">
                                        <h3 className="annual-tournament-detail__subsection-title">
                                            Самый голосуемый матч
                                        </h3>
                                        <div className="annual-tournament-detail__most-voted-info">
                                            <span>{stats.mostVotedMatch.participant1} vs {stats.mostVotedMatch.participant2}</span>
                                            <span className="annual-tournament-detail__most-voted-votes">
                                                {stats.mostVotedMatch.votes} голосов
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'leaderboard' && (
                    <div className="annual-tournament-detail__leaderboard">
                        <h2 className="annual-tournament-detail__section-title">
                            Лидерборд {tournament?.year}
                        </h2>

                        {leaderboardLoading ? (
                            <HrnbLoader />
                        ) : !leaderboard?.content || leaderboard.content.length === 0 ? (
                            <div className="annual-tournament-detail__leaderboard-empty">
                                <p>Лидерборд будет доступен после начала турнира</p>
                            </div>
                        ) : (
                            <>
                                <div className="annual-tournament-detail__leaderboard-list">
                                    {leaderboard.content.map((entry, index) => {
                                        const rank = entry.rank || index + 1;
                                        const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

                                        return (
                                            <div
                                                key={entry.profileId || index}
                                                className={`annual-tournament-detail__leaderboard-item ${rank <= 3 ? 'annual-tournament-detail__leaderboard-item--top' : ''}`}
                                            >
                                                <span className="annual-tournament-detail__leaderboard-rank">
                                                    {rankIcon}
                                                </span>
                                                <span className="annual-tournament-detail__leaderboard-name">
                                                    {entry.displayName || entry.profileName || 'Аноним'}
                                                </span>
                                                <span className="annual-tournament-detail__leaderboard-stats">
                                                    <span className="annual-tournament-detail__leaderboard-stat">
                                                        🎤 {entry.matches ?? 0} матчей
                                                    </span>
                                                    <span className="annual-tournament-detail__leaderboard-stat">
                                                        ✅ {entry.wins ?? 0} побед
                                                    </span>
                                                </span>
                                                <span className="annual-tournament-detail__leaderboard-points">
                                                    🏆 {entry.points ?? entry.score ?? 0} очк.
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Пагинация */}
                                {leaderboard.totalPages > 1 && (
                                    <div className="annual-tournament-detail__leaderboard-pagination">
                                        <HrnbButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setLeaderboardPage(prev => Math.max(0, prev - 1))}
                                            disabled={leaderboardPage === 0}
                                            icon={<BackIcon />}
                                        >
                                            Назад
                                        </HrnbButton>

                                        <span className="annual-tournament-detail__leaderboard-page">
                                            Страница {leaderboardPage + 1} из {leaderboard.totalPages}
                                        </span>

                                        <HrnbButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setLeaderboardPage(prev => Math.min(leaderboard.totalPages - 1, prev + 1))}
                                            disabled={leaderboardPage >= leaderboard.totalPages - 1}
                                            icon={<ForwardIcon />}
                                        >
                                            Вперёд
                                        </HrnbButton>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'bracket' && (
                    <div className="annual-tournament-detail__bracket">
                        {bracketLoading ? (
                            <HrnbLoader />
                        ) : bracket ? (
                            <TournamentBracket bracket={bracket} tournamentId={id} />
                        ) : (
                            <div className="annual-tournament-detail__bracket-empty">
                                <p>Сетка турнира будет доступна после начала</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'winners' && (
                    <div className="annual-tournament-detail__winners">
                        <h2 className="annual-tournament-detail__section-title">
                            Победители прошлых лет
                        </h2>
                        {winners.length > 0 ? (
                            <AnnualWinnersList winners={winners} />
                        ) : (
                            <div className="annual-tournament-detail__winners-empty">
                                <p>Победители будут доступны после завершения турниров</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Модальное окно регистрации */}
            <HrnbModal
                show={showRegisterModal}
                onHide={() => setShowRegisterModal(false)}
                title={`Регистрация на турнир ${tournament?.year}`}
            >
                <div className="annual-tournament-detail__modal">
                    <p>
                        Вы собираетесь зарегистрироваться на ежегодный турнир {tournament?.year} года.
                    </p>
                    <p className="annual-tournament-detail__modal-warning">
                        Внимание: регистрация окончательна и не может быть отменена после начала турнира.
                    </p>
                    
                    <div className="annual-tournament-detail__modal-actions">
                        <HrnbButton
                            variant="secondary"
                            onClick={() => setShowRegisterModal(false)}
                            icon={<RejectIcon />}
                        >
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="primary"
                            onClick={handleRegister}
                            disabled={actionLoading}
                            icon={<RegisterIcon />}
                        >
                            {actionLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>

            {/* Модальное окно отмены регистрации */}
            <HrnbModal
                show={showUnregisterModal}
                onHide={() => setShowUnregisterModal(false)}
                title="Отмена регистрации"
            >
                <p>Вы уверены, что хотите отменить регистрацию?</p>
                <div className="annual-tournament-detail__modal-actions">
                    <HrnbButton
                        variant="danger"
                        onClick={handleUnregister}
                        disabled={actionLoading}
                        icon={<RejectIcon />}
                    >
                        {actionLoading ? 'Отмена...' : 'Да, отменить'}
                    </HrnbButton>
                    <HrnbButton
                        variant="secondary"
                        onClick={() => setShowUnregisterModal(false)}
                        disabled={actionLoading}
                        icon={<BackIcon />}
                    >
                        Нет
                    </HrnbButton>
                </div>
            </HrnbModal>
        </div>
    );
}

export default AnnualTournamentDetailPage;
